import React from 'react';
import { Avatar, Box, Button, LinearProgress, Skeleton, Typography } from '@mui/material';
import { ArrowForward as ArrowIcon, Groups as GroupsIcon } from '@mui/icons-material';
import { formatHours } from '../monthly/format';
import { userInitials } from '../UserMenu';
import { cardSx, cardTitleSx, DAY_COLORS, GREEN, GREEN_DARK, INK, MUTED } from './homeTheme';

const TOP = 3;

/** Per i People Manager: quante persone della gerarchia hanno ore mancanti nel mese, e chi per primo. */
export default function TeamPulse({ team, onOpen }) {
  const { loading, error, summaries } = team;
  const people = summaries.length;
  const incomplete = summaries.filter((s) => s.missing > 0);
  const complete = people - incomplete.length;
  const missing = incomplete.reduce((sum, s) => sum + s.missing, 0);
  const top = [...incomplete].sort((a, b) => b.missing - a.missing).slice(0, TOP);

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <GroupsIcon sx={{ color: MUTED, fontSize: 20 }} />
        <Typography sx={{ ...cardTitleSx, flex: 1 }}>Your people</Typography>
        <Button size="small" endIcon={<ArrowIcon fontSize="small" />} onClick={onOpen} sx={{ textTransform: 'none', color: GREEN_DARK, fontWeight: 700 }}>
          Open
        </Button>
      </Box>

      {loading ? (
        <>
          <Skeleton width="60%" height={36} />
          <Skeleton height={14} />
          <Skeleton width="90%" />
          <Skeleton width="80%" />
        </>
      ) : error ? (
        <Typography sx={{ fontSize: 13, color: DAY_COLORS.missing }}>{error}</Typography>
      ) : !people ? (
        <Typography sx={{ fontSize: 13, color: MUTED }}>No people report to you.</Typography>
      ) : (
        <>
          <Typography sx={{ fontSize: 28, fontWeight: 800, color: INK, lineHeight: 1.1 }}>
            {complete}
            <Typography component="span" sx={{ fontSize: 16, fontWeight: 600, color: MUTED }}>
              {' '}
              / {people} complete
            </Typography>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(complete / people) * 100}
            sx={{ height: 8, borderRadius: 4, my: 1.25, bgcolor: 'var(--rt-cal-under)', '& .MuiLinearProgress-bar': { bgcolor: GREEN, borderRadius: 4 } }}
          />
          <Typography sx={{ fontSize: 12.5, color: MUTED, mb: 1.5 }}>
            {incomplete.length
              ? `${incomplete.length} with missing hours · ${formatHours(missing)}h in total up to yesterday`
              : 'Everyone has tracked their hours so far.'}
          </Typography>
          {top.map((s) => (
            <Box key={s.person.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.6 }}>
              <Avatar sx={{ width: 28, height: 28, fontSize: 11.5, fontWeight: 700, bgcolor: 'var(--rt-cal-under)', color: 'var(--rt-warn)' }}>
                {userInitials({ name: s.person.name })}
              </Avatar>
              <Typography noWrap sx={{ fontSize: 13, color: INK, flex: 1, minWidth: 0 }}>
                {s.person.name}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: DAY_COLORS.missing }}>{formatHours(s.missing)}h</Typography>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}
